// client/src/pages/Help.js
// Help Center page with organized articles by category
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Search } from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import HelpSection from '../components/help/HelpSection';
import SearchBar from '../components/help/SearchBar';
import helpContent from '../data/helpContent';

const Help = () => {
  const { t, i18n } = useTranslation(['help']);
  const [searchQuery, setSearchQuery] = useState('');

  // Get help content in current language
  const currentLanguage = i18n.language || 'en';
  const content = helpContent[currentLanguage] || helpContent.en;

  // Filter articles based on search
  const getFilteredContent = () => {
    if (!searchQuery.trim()) {
      return content;
    }

    const query = searchQuery.toLowerCase();
    const filtered = {};

    Object.keys(content).forEach(categoryKey => {
      const category = content[categoryKey];
      const filteredArticles = category.articles.filter(article =>
        article.title.toLowerCase().includes(query) ||
        article.content.toLowerCase().includes(query)
      );

      if (filteredArticles.length > 0) {
        filtered[categoryKey] = {
          ...category,
          articles: filteredArticles
        };
      }
    });

    return filtered;
  };

  const filteredContent = getFilteredContent();
  const hasResults = Object.keys(filteredContent).length > 0;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {currentLanguage === 'el' ? 'Κέντρο Βοήθειας' : 'Help Center'}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {currentLanguage === 'el' 
              ? 'Βρείτε απαντήσεις και οδηγούς για να αξιοποιήσετε στο έπακρο το WholesaleHub'
              : 'Find answers and guides to help you get the most out of WholesaleHub'}
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery('')}
              placeholder={currentLanguage === 'el' ? 'Αναζήτηση άρθρων βοήθειας...' : 'Search help articles...'}
            />
          </div>
        </div>

        {/* Quick Access Cards */}
        {!searchQuery && (
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
              <div className="text-3xl mb-3">🚀</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {currentLanguage === 'el' ? 'Ξεκινώντας' : 'Get Started'}
              </h3>
              <p className="text-gray-600 text-sm">
                {currentLanguage === 'el' 
                  ? 'Μάθετε πώς να ρυθμίσετε τον λογαριασμό σας και να ξεκινήσετε'
                  : 'Learn how to set up your account and get started'}
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
              <div className="text-3xl mb-3">📦</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {currentLanguage === 'el' ? 'Για Επιχειρήσεις' : 'For Businesses'}
              </h3>
              <p className="text-gray-600 text-sm">
                {currentLanguage === 'el' 
                  ? 'Οδηγοί παραγγελίας, διαχείρισης και βέλτιστων πρακτικών'
                  : 'Ordering guides, management tips, and best practices'}
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
              <div className="text-3xl mb-3">🏪</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {currentLanguage === 'el' ? 'Για Χονδρεμπόρους' : 'For Wholesalers'}
              </h3>
              <p className="text-gray-600 text-sm">
                {currentLanguage === 'el' 
                  ? 'Προσθήκη προϊόντων, διαχείριση παραγγελιών και ανάπτυξη'
                  : 'Product setup, order management, and growth tips'}
              </p>
            </div>
          </div>
        )}

        {/* Search Results Count */}
        {searchQuery && (
          <div className="mb-4 text-center text-gray-600">
            {currentLanguage === 'el' 
              ? `${Object.keys(filteredContent).length} κατηγορί${Object.keys(filteredContent).length === 1 ? 'α' : 'ες'} με σχετικά άρθρα`
              : `${Object.keys(filteredContent).length} categor${Object.keys(filteredContent).length === 1 ? 'y' : 'ies'} with matching articles`}
          </div>
        )}

        {/* Help Sections */}
        {hasResults ? (
          <div className="space-y-6">
            {Object.keys(filteredContent).map(categoryKey => {
              const category = filteredContent[categoryKey];
              return (
                <HelpSection
                  key={categoryKey}
                  title={category.title}
                  icon={category.icon}
                  articles={category.articles}
                  defaultOpen={!searchQuery || Object.keys(filteredContent).length <= 2}
                />
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">
              {currentLanguage === 'el' 
                ? 'Δεν βρέθηκαν άρθρα που να ταιριάζουν με την αναζήτησή σας.'
                : 'No articles found matching your search.'}
            </p>
            <p className="text-gray-400 mb-4">
              {currentLanguage === 'el' 
                ? 'Δοκιμάστε διαφορετικούς όρους αναζήτησης ή περιηγηθείτε σε όλες τις κατηγορίες.'
                : 'Try different search terms or browse all categories.'}
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              {currentLanguage === 'el' ? 'Εκκαθάριση αναζήτησης' : 'Clear search'}
            </button>
          </div>
        )}

        {/* Contact Support Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {currentLanguage === 'el' 
              ? 'Χρειάζεστε ακόμα βοήθεια;'
              : 'Still need help?'}
          </h2>
          <p className="text-gray-600 mb-6">
            {currentLanguage === 'el' 
              ? 'Η ομάδα υποστήριξής μας είναι εδώ για να απαντήσει σε οποιεσδήποτε ερωτήσεις έχετε.'
              : 'Our support team is here to answer any questions you have.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@wholesalehub.gr"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium inline-flex items-center justify-center gap-2"
            >
              <span>📧</span>
              <span>support@wholesalehub.gr</span>
            </a>
            <button
              onClick={() => window.location.href = '/faq'}
              className="px-6 py-3 bg-white text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
            >
              {currentLanguage === 'el' ? 'Δείτε Συχνές Ερωτήσεις' : 'View FAQs'}
            </button>
          </div>
        </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Help;